import type { Layer } from '@/features/draw/types'
import { CANVAS_SIZE } from '../../constants/canvas'

export const detectFace = (pixels: string[]) => {
  const width = CANVAS_SIZE
  const skinColors = new Set(['#FFC8A0', '#FFB080', '#E09060', '#FFD1B0', '#FFE0C0'])
  let minX = width,
    maxX = 0,
    minY = width,
    maxY = 0
  let hasSkin = false

  for (let y = 0; y < width; y++) {
    for (let x = 0; x < width; x++) {
      const color = pixels[y * width + x]
      if (skinColors.has(color)) {
        hasSkin = true
        minX = Math.min(minX, x)
        maxX = Math.max(maxX, x)
        minY = Math.min(minY, y)
        maxY = Math.max(maxY, y)
      }
    }
  }

  if (!hasSkin) return null

  return {
    x: minX,
    y: minY,
    width: maxX - minX + 1,
    height: maxY - minY + 1,
    centerX: Math.floor((minX + maxX) / 2),
    centerY: Math.floor((minY + maxY) / 2),
  }
}

export const getLayerColors = (layer: Layer): string[] => {
  const colors = new Set<string>()
  layer.pixels.forEach((color) => {
    if (color !== '') {
      colors.add(color)
    }
  })
  return Array.from(colors)
}

export const getUniqueColors = (layers: Layer[]) => {
  const colors = new Set()
  layers.forEach((layer) => {
    if (layer.visible) {
      layer.pixels.forEach((color) => {
        if (color !== '') {
          colors.add(color)
        }
      })
    }
  })
  return Array.from(colors)
}

export const getColorStatistics = (layers: Layer[]): Record<string, number> => {
  const stats: Record<string, number> = {}
  layers.forEach((layer) => {
    if (layer.visible) {
      layer.pixels.forEach((color) => {
        if (color !== '') {
          stats[color] = (stats[color] || 0) + 1
        }
      })
    }
  })
  return stats
}

export const canGenerateLayer = (layerId: number, layers: Layer[]) => {
  const layer = layers.find((l) => l.id === layerId)
  if (!layer) return false
  if (!layer.requiresPrevious) return true
  return hasLayerContent(layer.requiresPrevious, layers)
}

export const hasLayerContent = (layerId: number, layers: Layer[]) => {
  const layer = layers.find((l) => l.id === layerId)
  if (!layer) return false
  return layer.pixels.some((color) => color !== '')
}

export const isValidLayer = (layer: unknown): layer is Layer => {
  if (!layer || typeof layer !== 'object') return false
  const l = layer as Record<string, unknown>

  return (
    typeof l.id === 'string' &&
    typeof l.name === 'string' &&
    typeof l.visible === 'boolean' &&
    Array.isArray(l.pixels) &&
    l.pixels.every((pixel) => typeof pixel === 'string') &&
    (l.requiresPrevious === null || typeof l.requiresPrevious === 'string')
  )
}
